import React, { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Tab, TabState, TabContextValue, TabType } from '../types/tabs';
import { getJob } from '../../features/jobs/lib/jobs';
import { getEntry, getDataEntry } from '../../features/notebook/lib/entries';
import { supabase } from '../../shared/lib/supabase';

// Simple ID generation function
const generateId = () => {
  return `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

export const TabContext = createContext<TabContextValue | undefined>(undefined);

interface TabProviderProps {
  children: React.ReactNode;
  projectId: string;
}

export function TabProvider({ children, projectId }: TabProviderProps) {
  // Track tabs that are in the process of being created to prevent duplicates
  const pendingTabsRef = useRef<Set<string>>(new Set());
  const mountedRef = useRef(true);
  const cleanupTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const [state, setState] = useState<TabState>(() => {
    // Always start with a fresh state - no persistence
    return {
      tabs: [],
      activeTabId: null
    };
  });

  const [closingTabIds, setClosingTabIds] = useState<Set<string>>(new Set());

  // Cleanup function to clear timeouts
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
      }
    };
  }, []);

  // Safe setState function that checks if component is still mounted
  const safeSetState = useCallback((updater: React.SetStateAction<TabState>) => {
    if (mountedRef.current) {
      try {
        setState(updater);
      } catch (error) {
        console.warn('TabContext: setState failed, component may be unmounted:', error);
      }
    }
  }, []);

  // Clean up duplicate home and chat tabs per project and ensure correct closable properties
  useEffect(() => {
    if (!mountedRef.current) return;

    // Use a timeout to defer this operation to avoid conflicts with React's rendering
    cleanupTimeoutRef.current = setTimeout(() => {
      if (!mountedRef.current) return;

      safeSetState(prev => {
        try {
          // Group tabs by projectId
          const tabsByProject = new Map<string, Tab[]>();
          prev.tabs.forEach(tab => {
            const projectTabs = tabsByProject.get(tab.projectId) || [];
            projectTabs.push(tab);
            tabsByProject.set(tab.projectId, projectTabs);
          });
          
          let needsUpdate = false;
          const cleanTabs: Tab[] = [];
          
          // Process tabs for each project
          tabsByProject.forEach((projectTabs, projectId) => {
            const homeTabs = projectTabs.filter(tab => tab.type === 'home');
            const chatTabs = projectTabs.filter(tab => tab.type === 'chat');
            const otherTabs = projectTabs.filter(tab => tab.type !== 'home' && tab.type !== 'chat');
            
            // Check if we need to clean up duplicate tabs for this project
            if (homeTabs.length > 1) {
              needsUpdate = true;
            }
            
            if (chatTabs.length > 1) {
              needsUpdate = true;
            }
            
            // Keep only the first home and chat tabs for this project
            const firstHomeTab = homeTabs[0];
            const firstChatTab = chatTabs[0];
            
            // Add tabs for this project to the clean list
            if (firstHomeTab) {
              cleanTabs.push({ ...firstHomeTab, closable: false });
            }
            if (firstChatTab) {
              cleanTabs.push({ ...firstChatTab, closable: false });
            }
            
            // Ensure all other tabs have correct closable properties
            otherTabs.forEach(tab => {
              cleanTabs.push({ ...tab, closable: true });
            });
          });
          
          // Check if any tabs have incorrect closable properties
          const tabsNeedingClosableFix = prev.tabs.some(tab => {
            if (tab.type === 'home' || tab.type === 'chat') {
              return tab.closable !== false;
            } else {
              return tab.closable !== true;
            }
          });
          
          if (needsUpdate || tabsNeedingClosableFix || cleanTabs.length !== prev.tabs.length) {
            // Keep the same active tab if it's still in the clean list
            let newActiveTabId = prev.activeTabId;
            const activeTabStillExists = cleanTabs.some(tab => tab.id === prev.activeTabId);
            
            if (!activeTabStillExists && cleanTabs.length > 0) {
              // If active tab was removed, switch to the first available tab
              newActiveTabId = cleanTabs[0].id;
            }
            
            return {
              tabs: cleanTabs,
              activeTabId: newActiveTabId
            };
          }
          return prev;
        } catch (error) {
          console.warn('TabContext: Tab cleanup failed:', error);
          return prev;
        }
      });
    }, 0);

    return () => {
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
      }
    };
  }, [safeSetState]);

  // Tab state is no longer persisted - users get a fresh start each time

  // Define updateTab before loadTabData since loadTabData uses it
  const updateTab = useCallback((tabId: string, updates: Partial<Tab>) => {
    safeSetState(prev => ({
      ...prev,
      tabs: prev.tabs.map(t => 
        t.id === tabId ? { ...t, ...updates } : t
      )
    }));
  }, [safeSetState]);

  // Function to load tab data (name, etc) after tab creation
  const loadTabData = useCallback(async (tabId: string) => {
    const tab = state.tabs.find(t => t.id === tabId);
    if (!tab || !tab.loading) return;
    
    try {
      let newTitle = tab.title;
      let hasData = false;
      
      switch (tab.type) {
        case 'generation':
          if (tab.data?.jobId) {
            const job = await getJob(tab.data.jobId);
            if (job) {
              // Get project name if available
              let projectName = 'Project';
              
              // If job has project_id, fetch the project
              if (job.project_id) {
                try {
                  const { data: project } = await supabase
                    .from('projects')
                    .select('name')
                    .eq('id', job.project_id)
                    .single();
                  
                  if (project) {
                    projectName = project.name;
                  }
                } catch (error) {
                  console.error('Error fetching project:', error);
                }
              }
              
              // Format as "Job Name - Gen X"
              newTitle = `${job.name} - Gen ${job.generation}`;
              hasData = true;
            }
          }
          break;
          
        case 'entry':
          if (tab.data?.entryId) {
            const entry = await getEntry(tab.data.entryId);
            if (entry) {
              newTitle = entry.name;
              hasData = true;
            }
          }
          break;
          
        case 'table':
          if (tab.data?.dataEntryId) {
            const dataEntry = await getDataEntry(tab.data.dataEntryId);
            if (dataEntry) {
              newTitle = dataEntry.name;
              hasData = true;
            }
          }
          break;
          
        case 'odyssey':
          if (tab.data?.odysseyJobId) {
            // Load Odyssey job data
            const { data: odysseyJob } = await supabase
              .from('odyssey_jobs')
              .select('*')
              .eq('job_id', tab.data.odysseyJobId)
              .single();
            
            if (odysseyJob) {
              newTitle = odysseyJob.name || tab.data.odysseyJobId.substring(0, 8);
              hasData = true;
            }
          }
          break;

        case 'molecule':
          if (tab.data?.moleculeId) {
            // Load molecule viewer entry to get a stable name
            const { data: molecule, error: mErr } = await supabase
              .from('molecule_viewer')
              .select('name')
              .eq('id', tab.data.moleculeId)
              .single();
            if (!mErr && molecule) {
              newTitle = molecule.name || tab.data.runName || tab.title || 'Molecule';
              hasData = true;
            }
          }
          break;
      }
      
      // If no data was found, treat it as an error
      if (!hasData && tab.type !== 'home' && tab.type !== 'chat') {
        throw new Error('Data not found');
      }
      
      // Update the tab with the actual name
      updateTab(tabId, { title: newTitle, loading: false, error: undefined });
    } catch (error) {
      console.error(`Error loading data for ${tab.type} tab:`, error);
      // If loading fails, set an error state and ensure loading is false
      const fallbackTitle = tab.type === 'generation' ? 'Job not found' :
                          tab.type === 'entry' ? 'Entry not found' :
                          tab.type === 'table' ? 'Table not found' :
                          tab.type === 'odyssey' ? 'Odyssey job not found' :
                          tab.type === 'molecule' ? 'Molecule not found' : 'Unknown';
      updateTab(tabId, { 
        title: fallbackTitle, 
        loading: false, 
        error: 'Failed to load data'
      });
    }
  }, [state.tabs, updateTab]);

  const openTab = useCallback((tabData: Omit<Tab, 'id'>) => {
    if (!mountedRef.current) return;

    // Create a unique key for this tab to check pending status
    const tabKey = `${tabData.type}_${JSON.stringify(tabData.data)}`;
    
    // Check if this tab is already being created
    if (pendingTabsRef.current.has(tabKey)) {
      return;
    }
    
    // Mark this tab as pending
    pendingTabsRef.current.add(tabKey);
    
    safeSetState(prev => {
      try {
      // For home tabs, check if one already exists for this project
      if (tabData.type === 'home') {
        const existingHomeTab = prev.tabs.find(t => 
          t.type === 'home' && t.projectId === tabData.projectId
        );
        if (existingHomeTab) {
          // Just switch to existing home tab
          pendingTabsRef.current.delete(tabKey);
          return { ...prev, activeTabId: existingHomeTab.id };
        }
      }
      
      // For chat tabs, check if one already exists for this project
      if (tabData.type === 'chat') {
        const existingChatTab = prev.tabs.find(t => 
          t.type === 'chat' && t.projectId === tabData.projectId
        );
        if (existingChatTab) {
          // Just switch to existing chat tab
          pendingTabsRef.current.delete(tabKey);
          return { ...prev, activeTabId: existingChatTab.id };
        }
      }
      
      // For other tabs, check if this specific item is already open
      const existingTab = prev.tabs.find(t => 
        t.type === tabData.type && 
        t.projectId === tabData.projectId &&
        JSON.stringify(t.data) === JSON.stringify(tabData.data)
      );
      
      if (existingTab) {
        // Just switch to existing tab
        pendingTabsRef.current.delete(tabKey);
        return { ...prev, activeTabId: existingTab.id };
      }
      
      // Create new tab - ensure correct closable properties
      const newTab: Tab = {
        ...tabData,
        id: generateId(),
        projectId,
        closable: (tabData.type === 'home' || tabData.type === 'chat') ? false : true
      };
      
        // Remove from pending after a short delay
        setTimeout(() => {
          if (mountedRef.current) {
            pendingTabsRef.current.delete(tabKey);
          }
        }, 100);
        
        // When creating a chat tab, don't automatically make it active if a home tab exists
        let shouldActivate = true;
        if (tabData.type === 'chat') {
          const homeTabExists = prev.tabs.some(t => 
            t.type === 'home' && t.projectId === tabData.projectId
          );
          if (homeTabExists) {
            shouldActivate = false;
          }
        }
        
        return {
          tabs: [...prev.tabs, newTab],
          activeTabId: shouldActivate ? newTab.id : prev.activeTabId
        };
      } catch (error) {
        console.warn('TabContext: Error in openTab:', error);
        pendingTabsRef.current.delete(tabKey);
        return prev;
      }
    });
  }, [projectId, safeSetState]);

  // Effect to automatically load tab data when a tab is marked as loading
  useEffect(() => {
    const timeouts: { [key: string]: NodeJS.Timeout } = {};
    
    state.tabs.forEach(tab => {
      if (tab.loading && !timeouts[tab.id]) {
        // Load tab data
        loadTabData(tab.id);
        
        // Set a timeout to ensure tab doesn't stay in loading state forever
        timeouts[tab.id] = setTimeout(() => {
          const currentTab = state.tabs.find(t => t.id === tab.id);
          if (currentTab?.loading) {
            console.warn(`Tab ${tab.id} loading timeout`);
            updateTab(tab.id, {
              title: 'Load timeout',
              loading: false,
              error: 'Loading took too long'
            });
          }
        }, 10000); // 10 second timeout
      }
    });
    
    // Cleanup timeouts
    return () => {
      Object.values(timeouts).forEach(timeout => clearTimeout(timeout));
    };
  }, [state.tabs, loadTabData, updateTab]);

  const closeTab = useCallback((tabId: string) => {
    if (!mountedRef.current) return Promise.resolve();

    
    // Check if tab is already being closed
    if (closingTabIds.has(tabId)) {
      return Promise.resolve();
    }
    
    // Mark tab as closing
    setClosingTabIds(prev => new Set([...prev, tabId]));
    
    return new Promise<void>((resolve) => {
      safeSetState(prev => {
        try {
          
          const tabToClose = prev.tabs.find(t => t.id === tabId);
          const tabIndex = prev.tabs.findIndex(t => t.id === tabId);
          
          if (tabIndex === -1) {
            console.warn('TabContext: Tab not found:', tabId);
            // Remove from closing set
            setClosingTabIds(prev => {
              const newSet = new Set(prev);
              newSet.delete(tabId);
              return newSet;
            });
            resolve();
            return prev;
          }
          
          // Don't allow closing home or chat tabs
          if ((tabToClose?.type === 'home' || tabToClose?.type === 'chat') && !tabToClose.closable) {
            console.warn('TabContext: Cannot close home or chat tab');
            // Remove from closing set
            setClosingTabIds(prev => {
              const newSet = new Set(prev);
              newSet.delete(tabId);
              return newSet;
            });
            resolve();
            return prev;
          }
          
          
          const newTabs = prev.tabs.filter(t => t.id !== tabId);
          let newActiveTabId = prev.activeTabId;
          
          // If we're closing the active tab, switch to adjacent tab
          if (prev.activeTabId === tabId) {
            if (newTabs.length > 0) {
              // Prefer switching to home tab if available
              const homeTab = newTabs.find(t => t.type === 'home' && t.projectId === tabToClose?.projectId);
              if (homeTab) {
                newActiveTabId = homeTab.id;
              } else {
                // Otherwise, try to activate the tab to the left (more intuitive)
                const newIndex = Math.max(0, Math.min(tabIndex - 1, newTabs.length - 1));
                newActiveTabId = newTabs[newIndex].id;
              }
            } else {
              newActiveTabId = null;
            }
          }
          
          const newState = {
            tabs: newTabs,
            activeTabId: newActiveTabId
          };
          
          
          // Remove from closing set after state update
          setTimeout(() => {
            if (mountedRef.current) {
              setClosingTabIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(tabId);
                return newSet;
              });
              resolve();
            }
          }, 100);
          
          return newState;
        } catch (error) {
          console.warn('TabContext: Error in closeTab:', error);
          // Clean up on error
          setClosingTabIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(tabId);
            return newSet;
          });
          resolve();
          return prev;
        }
      });
    });
  }, [closingTabIds]);

  const switchTab = useCallback((tabId: string) => {
    setState(prev => {
      if (prev.tabs.some(t => t.id === tabId)) {
        return { ...prev, activeTabId: tabId };
      }
      return prev;
    });
  }, []);

  const getTabById = useCallback((tabId: string) => {
    return state.tabs.find(t => t.id === tabId);
  }, [state.tabs]);

  const isTabOpen = useCallback((type: TabType, data?: any) => {
    return state.tabs.find(t => 
      t.type === type && 
      (!data || JSON.stringify(t.data) === JSON.stringify(data))
    );
  }, [state.tabs]);

  const reorderTabs = useCallback((newTabs: Tab[]) => {
    setState(prev => ({
      ...prev,
      tabs: newTabs
    }));
  }, []);

  // Function to filter tabs to only show those from a specific project
  const filterTabsByProject = useCallback((projectId: string) => {
    setState(prev => {
      const projectTabs = prev.tabs.filter(tab => tab.projectId === projectId);
      
      // If we're filtering out the active tab, switch to the first tab of the new project
      let newActiveTabId = prev.activeTabId;
      const activeTabInProject = projectTabs.some(tab => tab.id === prev.activeTabId);
      
      if (!activeTabInProject && projectTabs.length > 0) {
        // Find the home tab for this project, or fallback to first tab
        const homeTab = projectTabs.find(tab => tab.type === 'home');
        newActiveTabId = homeTab ? homeTab.id : projectTabs[0].id;
      } else if (projectTabs.length === 0) {
        newActiveTabId = null;
      }
      
      
      return {
        tabs: projectTabs,
        activeTabId: newActiveTabId
      };
    });
  }, []);

  const activeTab = useMemo(() => {
    return state.activeTabId ? getTabById(state.activeTabId) || null : null;
  }, [state.activeTabId, getTabById]);

  const contextValue = useMemo<TabContextValue>(() => ({
    tabs: state.tabs,
    activeTabId: state.activeTabId,
    activeTab,
    openTab,
    closeTab,
    switchTab,
    updateTab,
    getTabById,
    isTabOpen,
    reorderTabs,
    filterTabsByProject
  }), [
    state.tabs, 
    state.activeTabId, 
    activeTab, 
    openTab, 
    closeTab, 
    switchTab, 
    updateTab, 
    getTabById, 
    isTabOpen, 
    reorderTabs, 
    filterTabsByProject
  ]);

  return (
    <TabContext.Provider value={contextValue}>
      {children}
    </TabContext.Provider>
  );
}

export function useTabManager() {
  const context = useContext(TabContext);
  if (!context) {
    throw new Error('useTabManager must be used within a TabProvider');
  }
  return context;
}

// Hook to get current active tab
export function useActiveTab() {
  const { activeTab } = useTabManager();
  return activeTab;
}

// Hook to check if a specific tab is active
export function useIsTabActive(tabId: string) {
  const { activeTabId } = useTabManager();
  return activeTabId === tabId;
}
